/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { StreamingLiveCommerceAddressV2 } from "./StreamingLiveCommerceAddressV2";
import { StreamingLiveCommercePaymentMethodV2 } from "./StreamingLiveCommercePaymentMethodV2";
import { StreamingLiveCommerceItemListV2 } from "./StreamingLiveCommerceItemListV2";
import { StreamingLiveCommerceAmountV2 } from "./StreamingLiveCommerceAmountV2";
import { SETTLEMENT_FEE_DATA } from "constants/purchase";
import { useLiveCommerceShippingFee } from "hooks/Settlement";
import { useI18n } from "hooks/i18n/i18n";
import React, { useEffect } from "react";
import { useSelector } from "store";
import { TextLink } from "ui/TextLink";
import { scrollTop } from "utility/index";

import type { PayType } from "@spwn/types";
import type { ProductData } from "@spwn/types/firebase/firestore";
import type { Store } from "store";
import { StreamingLiveCommerceCartLayoutV2 } from "./StreamingLiveCommerceCartLayoutV2";

type Props = {
  currentVideoId: string;
  THEOPlayerElementHeight: number;
  pseudoCart: (ProductData & { count: number })[];
  incrementCartItem: (itemData: ProductData) => void;
  decrementCartItem: (itemData: ProductData) => void;
  removeCartItem: (itemData: ProductData) => void;
  closeLiveCommerceCart: () => void;
};

const StreamingLiveCommerceCartV2: React.FC<Props> = (props) => {
  const [paymentMethod, setPaymentMethod] = React.useState<PayType>("Card");
  const [totalFee, setTotalFee] = React.useState<number>(0);
  const [settlementFee, setSettlementFee] = React.useState<number>(0);

  // PawCharge component supposed to call fetchCardInfo to fetch cardInfoList
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const displayEvent = useSelector((state: Store) => state.event.displayEvent);
  const registeredCardList = useSelector(
    (state: Store) => state.purchase.cardInfoList
  );
  // didMountとにwatchPseudoCartに移動する
  // それに伴ってStateに値を格納するように変更する
  const { isCalculatingShippingFee, shippingFee, isError } =
    useLiveCommerceShippingFee(displayEvent, props.pseudoCart);

  const { t } = useI18n();
  const classesLiveCommerceCartDesign = liveCommerceCartDesign({
    isDarkModeEnabled,
    THEOPlayerElementHeight: props.THEOPlayerElementHeight,
  });

  const didMount = () => {
    scrollTop();
  };
  useEffect(didMount, []);
  const cartTotalFee = props.pseudoCart.reduce(
    (sum, item) => sum + item.price_jpy * item.count,
    0
  );

  const watchPseudoCart = () => {
    if (props.pseudoCart.length === 0) {
      setTotalFee(0);
    } else {
      let total = props.pseudoCart
        .map((el) => el.price_jpy * el.count)
        .reduce((sum, item) => sum + item);
      total += shippingFee;
      total += settlementFee;
      setTotalFee(total);
    }
  };
  useEffect(watchPseudoCart, [props.pseudoCart, shippingFee, settlementFee]);

  const watchSettlementFee = () => {
    setSettlementFee(SETTLEMENT_FEE_DATA[paymentMethod]);
  };
  useEffect(watchSettlementFee, [paymentMethod]);

  const removeCartItem = (itemData: ProductData) => {
    props.removeCartItem(itemData);
  };

  const headerLeft = <ArrowBackIosIcon onClick={props.closeLiveCommerceCart} />;
  const headerCenter = (
    <p>
      {t("streaming.commerceCart.total")} : ¥{totalFee.toLocaleString()}
    </p>
  );
  const headerRight = (
    <div css={classesLiveCommerceCartDesign.confirmButton}>
      {t("streaming.commerceCart.proceedToCheckout")}
    </div>
  );
  const addressSection = <StreamingLiveCommerceAddressV2 />;
  const paymentSection = (
    <StreamingLiveCommercePaymentMethodV2
      paymentMethod={paymentMethod}
      registeredCardList={registeredCardList}
      setPaymentMethod={setPaymentMethod}
    />
  );
  const cartLabel = t("streaming.commerceCart.order");
  const itemListSection = (
    <StreamingLiveCommerceItemListV2
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
      isDarkModeEnabled={isDarkModeEnabled}
      pseudoCart={props.pseudoCart}
      displayEvent={displayEvent}
      removeCartItem={removeCartItem}
    />
  );

  const summarySection = (
    <StreamingLiveCommerceAmountV2
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
      isDarkModeEnabled={isDarkModeEnabled}
      cartTotalFee={cartTotalFee}
      shippingFee={shippingFee}
      settlementFee={settlementFee}
      totalFee={totalFee}
      isCalculatingShippingFee={isCalculatingShippingFee}
    />
  );

  const footerSection = isError ? (
    <div css={classesLiveCommerceCartDesign.button}>
      <p>{t("streaming.commerceCart.noticeInvalidAddress")}</p>
      <TextLink href={`https://${window.location.hostname}/account/address`}>
        {t("streaming.commerceCart.editAddress")}
      </TextLink>
    </div>
  ) : null;

  return (
    <StreamingLiveCommerceCartLayoutV2
      isDarkMode={isDarkModeEnabled}
      playerHeight={props.THEOPlayerElementHeight}
      headerLeft={headerLeft}
      headerCenter={headerCenter}
      headerRight={headerRight}
      addressSection={addressSection}
      paymentSection={paymentSection}
      cartLabel={cartLabel}
      itemListSection={itemListSection}
      summarySection={summarySection}
      footerSection={footerSection}
    />
  );
};
type LiveCommerceCartDesignProps = {
  THEOPlayerElementHeight: number;
  isDarkModeEnabled: boolean;
};
const liveCommerceCartDesign = (props: LiveCommerceCartDesignProps) => {
  const confirmButton = css`
    padding: 6px 12px;
    background-color: ${props?.isDarkModeEnabled ? "#000000" : "#ffffff"};
    color: ${props?.isDarkModeEnabled ? "#ffffff" : "#000000"};
    border: ${props?.isDarkModeEnabled ? "none" : "1px solid #000000"};
    font-weight: bold;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
  `;
  // const categoryHeader = css`
  //   padding: 8px 0;
  //   border-bottom: 1px solid #e0e0e0;
  //   background-color: #f4f4f4;
  //   p{
  //     padding: 0 12px;
  //     font-size: 15px;
  //     font-weight: bold;
  //   }
  // `
  const button = css`
    padding: 12px 0;
    p {
      font-size: 14px;
      line-height: 1.5em;
      margin-bottom: 8px;
    }
    a {
      display: block;
      width: 100%;
      padding: 12px 0;
      color: #fff;
      font-size: 14px;
      text-align: center;
      text-decoration: none;
      background-color: #00c2ae;
      border: none;
      outline: none;
      appearance: none;
      cursor: pointer;
      &.disabled {
        background-color: #b1b1b1;
      }
    }
  `;
  return {
    confirmButton,
    button,
  };
};

export default StreamingLiveCommerceCartV2;
